import { Avatar, Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { mediaSourceUrl } from './utils'
import { IconProjectPlaceholder } from '../../../icons/IconProjectPlaceholder'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { NotificationTypeChip } from './NotificationTypeChip'
import { createNotificationNavHandlers, getActorPathKey, getProjectSlug, getTeamSlug } from './notificationNav'
import { ActorNameSpan, ProjectNameQuoted } from './notificationTypography'
import { NotificationDecisionStatus } from './NotificationDecisionStatus'
import { notificationBodyWrapSx, notificationMessageClampSx } from './notificationTextClamp'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'project.team_invitation.decided' }>
}

export function TeamInvitationDecidedNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onProjectClick, onUserClick, onTeamClick } = createNotificationNavHandlers(navigate)
  const payload = notification.payload
  const project = payload?.project ?? {}
  const team = payload?.team ?? {}
  const actor = payload?.actor ?? {}
  const invitation = payload?.invitation ?? {}
  const projectTitle = typeof project.title === 'string' && project.title.trim().length > 0 ? project.title : 'проект'
  const teamName = typeof team.name === 'string' && team.name.trim().length > 0 ? team.name : 'команда'
  const actorName = typeof actor.fullname === 'string' ? actor.fullname : 'Пользователь'
  const status = typeof invitation.status === 'string' ? invitation.status : 'unknown'
  const projectAvatarSrc = mediaSourceUrl(project.image_url) ?? undefined
  const projectSlug = getProjectSlug(project)
  const teamSlug = getTeamSlug(team)
  const actorPathKey = getActorPathKey(actor, notification.actor_user_id)
  const responseMessage =
    typeof invitation.response_message === 'string' ? invitation.response_message.trim() : ''

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onProjectClick(e, projectSlug)}
          sx={{ cursor: projectSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={projectTitle}
            avatarSrc={projectAvatarSrc}
            fallbackIcon={<IconProjectPlaceholder fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle={
              <>
                <Typography variant="black-12-light" component="span" sx={{ minWidth: 0, ...notificationBodyWrapSx }}>
                  Ответ команды «{teamName}»
                </Typography>
                <NotificationTypeChip variant="team_invitation_decided" />
              </>
            }
          />
        </Box>

        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
            sx={{ cursor: actorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
          >
            <Avatar
              src={mediaSourceUrl(actor.avatar_url) ?? undefined}
              alt={actorName}
              sx={{ width: 34, height: 34 }}
            />
          </Box>
          <Stack minWidth={0} flex={1} gap={0.25}>
            <Stack direction="row" gap={0.5} alignItems="baseline" minWidth={0} flexWrap="wrap">
              <Typography variant="black-14-normal" component="span" sx={notificationBodyWrapSx}>
                Команда <ProjectNameQuoted name={teamName} pathKey={teamSlug} onNameClick={onTeamClick} /> — приглашение в{' '}
                <ProjectNameQuoted name={projectTitle} pathKey={projectSlug} onNameClick={onProjectClick} />
              </Typography>
              <NotificationDecisionStatus status={status} />
            </Stack>
            {responseMessage ? (
              <Typography variant="black-12-light" sx={notificationMessageClampSx}>
                {responseMessage}
              </Typography>
            ) : (
              <Typography variant="black-12-light">
                Ответ от{' '}
                <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
                  {actorName}
                </ActorNameSpan>
              </Typography>
            )}
          </Stack>
        </Stack>

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
